import { AnimatePresence, motion } from 'framer-motion';
import React, {
  FC,
  MouseEvent,
  MouseEventHandler,
  useContext,
  useState,
} from 'react';
import Ripples from 'react-ripples';
import { ProductLineItemType } from '../../../models/product-line-item-type';
import { ProductCardLabelsType } from '../../../models/product-card-labels-type';
import { ProductDetailResultType } from '../../../models/product-detail-result-type';
import IcomoonIcon from '../../atoms/icomoon-icon/icomoon-icon';
import open from '../../providers/overlay/open.overlay.actions';
import { OverlayDispatchContext } from '../../providers/overlay/overlay.provider';
// eslint-disable-next-line import/no-cycle
import FavouriteBasketsAddModal from '../favourite-baskets-add-modal/favourite-baskets-add-modal';
import getTailwindConfig from '../../../helpers/get-tailwind-config';

interface Props {
  // eslint-disable-next-line prettier/prettier
  readonly basketItem: ProductLineItemType | ProductDetailResultType;
  readonly labels: ProductCardLabelsType;
  readonly onAddClick?: MouseEventHandler;
}

const AddToFavouritesButton: FC<Props> = ({
  basketItem,
  labels,
  onAddClick,
}: Props) => {
  const [addedToBasket, setAddedToBasket] = useState<boolean>(false);
  const [page, setPage] = useState<0 | 1>(0);
  const overlayDispatch = useContext(OverlayDispatchContext);
  const fullConfig = getTailwindConfig();
  const frenchBlue = fullConfig.theme.colors['french-blue'];

  const addToBasket = (): void => {
    setAddedToBasket(true);

    setTimeout(() => setAddedToBasket(false), 2000);
  };

  const openFavouriteBasketsAddModal = (event: MouseEvent): void => {
    onAddClick(event);

    overlayDispatch(
      open(
        <FavouriteBasketsAddModal
          basketItem={basketItem}
          labels={labels}
          onSubmit={() => addToBasket()}
        />
      )
    );
  };

  return (
    <>
      <AnimatePresence onExitComplete={() => setPage(1)}>
        {!addedToBasket && page === 0 && (
          <Ripples
            className="rounded-full"
            color={`rgba(${frenchBlue.rgb}, .2)`}
            during={1200}
          >
            <motion.button
              animate={{ opacity: 1 }}
              className="flex items-center focus:outline-none px-4 py-2 rounded-full"
              exit={{ opacity: 0 }}
              initial={{ opacity: 1 }}
              onClick={(event) => openFavouriteBasketsAddModal(event)}
              type="button"
            >
              <IcomoonIcon color="french-blue" name="star-outline" size="lg" />

              <span className="ml-2 text-french-blue">
                {labels.addToFavouritesLabel}
              </span>
            </motion.button>
          </Ripples>
        )}
      </AnimatePresence>

      <AnimatePresence onExitComplete={() => setPage(0)}>
        {addedToBasket && page === 1 && (
          <motion.div
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center px-4 py-2"
            exit={{ opacity: 0, scale: 1 }}
            initial={{ opacity: 0, scale: 0.75 }}
            onClick={(event) => openFavouriteBasketsAddModal(event)}
          >
            <IcomoonIcon color="french-blue" name="star" size="lg" />

            <span className="ml-2 text-french-blue">
              {labels.addedToFavouritesLabel}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

AddToFavouritesButton.defaultProps = {
  onAddClick: () => ({}),
};

export default AddToFavouritesButton;
